// Quem está logado, e o que acontece quando deixa de estar.
//
// Três estados, não dois: `carregando` existe porque no boot o token está no
// localStorage mas `/api/auth/me` ainda não respondeu. Tratar isso como
// "deslogado" mostraria a tela de login por um instante a cada F5 — e quem
// digitasse a senha nesse instante perderia o que estava fazendo.
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { aoExpirar, entrar, pegar } from '../api/cliente';
import { esquecerCredenciais, guardarCredenciais, lerCredenciais } from '../api/tokens';
import type { Eu } from '../api/tipos';

interface Sessao {
  estado: 'carregando' | 'dentro' | 'fora';
  eu: Eu | null;
  admin: boolean;
  logar: (email: string, senha: string) => Promise<void>;
  sair: () => void;
}

const Contexto = createContext<Sessao | null>(null);

export function useSessao(): Sessao {
  const s = useContext(Contexto);
  if (!s) throw new Error('useSessao fora do ProvedorDeSessao');
  return s;
}

export function ProvedorDeSessao({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<Sessao['estado']>(() => (lerCredenciais() ? 'carregando' : 'fora'));
  const [eu, setEu] = useState<Eu | null>(null);
  const fila = useQueryClient();

  const sair = useCallback(() => {
    esquecerCredenciais();
    setEu(null);
    setEstado('fora');
    // sem limpar o cache, o próximo login veria por um instante o acervo do
    // usuário anterior — que pode não ter acesso aos mesmos projetos
    fila.clear();
  }, [fila]);

  // o refresh que falha acontece no meio de qualquer requisição, longe daqui
  useEffect(() => aoExpirar(sair), [sair]);

  const carregarEu = useCallback(async () => {
    const meu = await pegar<Eu>('/api/auth/me');
    setEu(meu);
    setEstado('dentro');
  }, []);

  useEffect(() => {
    if (estado !== 'carregando') return;
    void carregarEu().catch(() => sair());
  }, [estado, carregarEu, sair]);

  const logar = useCallback(async (email: string, senha: string) => {
    const cred = await entrar(email, senha);
    guardarCredenciais(cred);
    await carregarEu();
  }, [carregarEu]);

  const valor = useMemo<Sessao>(
    () => ({ estado, eu, admin: eu?.role === 'admin', logar, sair }),
    [estado, eu, logar, sair],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}
